// src/hooks/useHistoryForm.js
"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import { fetchMedicalInfoByUserId, updateMedicalInfo } from "../redux/thunks/medicalThunks";
import {
  fetchSurgicalHistory,
  addSurgicalHistory,
  updateSurgicalHistory,
  removeSurgicalHistory,
} from "../redux/thunks/surgicalHistoryThunks";
import {
  fetchAllergies,
  addAllergyRecord,
  updateAllergyRecord,
  removeAllergyRecord,
} from "../redux/thunks/allergyThunks";
import {
  fetchFamilyHistory,
  addFamilyHistory,
  updateFamilyHistory,
  removeFamilyHistory,
} from "../redux/thunks/familyHistoryThunks";
import {
  fetchPersonalHistoryByUserId,
  addPersonalHistory,
  updatePersonalHistory,
} from "../redux/thunks/personalHistoryThunks";

import { selectMedicalData } from "../redux/selectors/medicalSelectors";
import { selectSurgicalData } from "../redux/selectors/surgicalHistorySelectors";
import { selectFamilyData } from "../redux/selectors/familyHistorySelectors";
import { selectPersonalHistoryItems } from "../redux/selectors/personalHistorySelectors";

const lifestyleStatusFromValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "never") return "NEVER";
  if (normalized === "occasionally") return "OCCASIONAL";
  if (["daily", "weekly"].includes(normalized)) return "REGULAR";
  return "UNKNOWN";
};

/**
 * ✅ NEW: support useHistoryForm(userId, { enabled })
 */
export function useHistoryForm(userId, options = {}) {
  const { enabled = true } = options;

  const dispatch = useDispatch();

  const medicalData = useSelector(selectMedicalData);
  const surgicalData = useSelector(selectSurgicalData);
  const familyData = useSelector(selectFamilyData);
  const personalHistoryItems = useSelector(selectPersonalHistoryItems);

  const [lifestyle, setLifestyle] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [allergyRecords, setAllergyRecords] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [familyHistory, setFamilyHistory] = useState([]);
  const [chronic, setChronic] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ===== initial fetch ===== */
  useEffect(() => {
    if (!enabled) return;
    if (!userId) return;

    setLoading(true);
    Promise.all([
      dispatch(fetchMedicalInfoByUserId(userId)),
      dispatch(fetchSurgicalHistory(userId)),
      dispatch(fetchFamilyHistory(userId)),
      dispatch(fetchPersonalHistoryByUserId(userId)),
      dispatch(fetchAllergies(userId)).unwrap()
        .then(setAllergyRecords)
        .catch((error) => {
          message.error(error?.message || "Failed to load allergies");
        }),
    ]).finally(() => setLoading(false));
  }, [dispatch, userId, enabled]);

  /* latest entry from timeline (personal history) */
  const lastTimelineEntry = useCallback(
    (type) => {
      const normalizedType = String(type || "").toUpperCase();
      return (personalHistoryItems || []).find(
        (item) => String(item?.type || "").toUpperCase() === normalizedType
      ) || null;
    },
    [personalHistoryItems]
  );

  /* ===== lifestyle mapping (timeline is authoritative; snapshot is legacy fallback) ===== */
  useEffect(() => {
    if (!enabled) return;

    setLifestyle(() => {
      const build = (type, label, medVal) => {
        const timelineEntry = lastTimelineEntry(type);
        const level = timelineEntry?.value ?? medVal ?? "Occasionally";
        return {
          id: type,
          type,
          label,
          active:
            typeof timelineEntry?.isActive === "boolean"
              ? timelineEntry.isActive
              : !!(medVal && String(medVal).trim()),
          level,
          backendId: timelineEntry?.id || null,
        };
      };

      return [
        build("SMOKING", "Smoking", medicalData?.tabac),
        build("ALCOHOL", "Alcohol", medicalData?.alcool),
        build("SPORT", "Physical Activity", medicalData?.activitePhysique),
      ];
    });
  }, [medicalData, lastTimelineEntry, enabled]);

  /* ===== persist lifestyle level ===== */
  const saveLifestyle = useCallback(
    async (item) => {
      if (!enabled) return;
      if (!userId) return;

      const addBody = {
        type: item.type,
        value: item.level,
        status: lifestyleStatusFromValue(item.level),
        isActive: Boolean(item.active),
      };
      const updBody = {
        value: item.level,
        status: lifestyleStatusFromValue(item.level),
        isActive: Boolean(item.active),
      };

      try {
        let saved;
        if (item.backendId) {
          saved = await dispatch(
            updatePersonalHistory({ userId, id: item.backendId, values: updBody })
          ).unwrap();
        } else {
          saved = await dispatch(addPersonalHistory({ userId, values: addBody })).unwrap();
        }

        await dispatch(fetchPersonalHistoryByUserId(userId));
        return saved;
      } catch (err) {
        message.error(err?.message || `Failed to save ${item.label?.toLowerCase?.() || "item"}`);
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  /* ===== toggle ON/OFF (indépendant du level) ===== */
  const setLifestyleActive = useCallback(
    async (rowId, active) => {
      if (!enabled) return;
      if (!userId) return;

      const row = lifestyle.find((x) => x.id === rowId);
      if (!row) return;

      const desiredValue = row.level || lastTimelineEntry(row.type)?.value || "Occasionally";

      try {
        const saved = await saveLifestyle({
          ...row,
          level: desiredValue,
          active,
        });

        setLifestyle((prev) =>
          prev.map((x) =>
            x.id === rowId
              ? {
                  ...x,
                  active,
                  level: desiredValue,
                  backendId: saved?.id ?? saved?._id ?? x.backendId,
                }
              : x
          )
        );

        message.success(`${row.label} ${active ? "enabled" : "disabled"}`);
      } catch (_) {}
    },
    [userId, lifestyle, lastTimelineEntry, saveLifestyle, enabled]
  );

  const updateLifestyle = useCallback(
    (id, patch) => {
      if (!enabled) return;

      setLifestyle((prev) => {
        const before = prev.find((x) => x.id === id);
        const next = prev.map((x) => (x.id === id ? { ...x, ...patch } : x));
        const after = next.find((x) => x.id === id);
        if (!before || !after) return next;

        if ("level" in patch && before.level !== after.level) {
          saveLifestyle(after);
        }
        if ("active" in patch && before.active !== after.active) {
          setLifestyleActive(id, after.active);
        }
        return next;
      });
    },
    [saveLifestyle, setLifestyleActive, enabled]
  );

  /* ===== Allergies & Surgeries ===== */
  useEffect(() => {
    if (!enabled) return;
    if (!Array.isArray(surgicalData)) return;

    setAllergies(
      [
        ...surgicalData
        .filter((s) => s.type === "ALLERGY")
        .map((a) => ({
          id: `legacy-${a.id}`,
          name: a.item,
          reactionDescription: a.reaction || "",
          severityLevel: "MILD",
          category: "DRUG",
          backendId: a.id,
          model: "surgicalHistory",
        })),
        ...allergyRecords.map((allergy) => ({
          id: `allergy-${allergy.id}`,
          backendId: allergy.id,
          model: "allergy",
          category: allergy.category,
          name: allergy.category === "DRUG"
            ? allergy.medicationName || ""
            : allergy.category === "FOOD"
              ? allergy.foodName || ""
              : allergy.allergen || "",
          reactionDescription: allergy.reactionDescription || "",
          severityLevel: allergy.severityLevel || "MILD",
        })),
      ]
    );

    setSurgeries(
      surgicalData
        .filter((s) => s.type === "SURGERY")
        .map((s) => ({
          id: s.id,
          type: s.item,
          year: s.date || "",
          backendId: s.id,
        }))
    );
  }, [surgicalData, allergyRecords, enabled]);

  /* ===== Allergies ===== */
  const saveAllergy = useCallback(
    async (allergy) => {
      if (!enabled) return;
      if (!userId || allergy.backendId) return;
      try {
        const category = allergy.category || "DRUG";
        const values = {
          category,
          severityLevel: allergy.severityLevel || "MILD",
          ...(category === "DRUG"
            ? {
                medicationName: allergy.name.trim(),
                reactionDescription: allergy.reactionDescription.trim(),
              }
            : category === "FOOD"
              ? { foodName: allergy.name.trim() }
              : { allergen: allergy.name.trim() }),
        };
        const created = allergy.model === "surgicalHistory"
          ? await dispatch(addSurgicalHistory({
              userId,
              values: {
                type: "ALLERGY",
                item: allergy.name,
                reaction: allergy.reactionDescription,
              },
            })).unwrap()
          : await dispatch(addAllergyRecord({ userId, values })).unwrap();
        message.success("Allergy added");
        if (allergy.model === "surgicalHistory") {
          await dispatch(fetchSurgicalHistory(userId));
        } else {
          setAllergyRecords(await dispatch(fetchAllergies(userId)).unwrap());
        }
        return created;
      } catch (err) {
        message.error(err?.message || "Failed to add allergy");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  const updateAllergyBackend = useCallback(
    async (id, values, model = "allergy") => {
      if (!enabled) return;
      if (!userId) return;
      try {
        const updated = model === "surgicalHistory"
          ? await dispatch(updateSurgicalHistory({ userId, id, values })).unwrap()
          : await dispatch(updateAllergyRecord({ userId, id, values })).unwrap();
        if (model === "surgicalHistory") {
          await dispatch(fetchSurgicalHistory(userId));
        } else {
          setAllergyRecords(await dispatch(fetchAllergies(userId)).unwrap());
        }
        return updated;
      } catch (err) {
        message.error(err?.message || "Failed to update allergy");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  const deleteAllergy = useCallback(
    async (id, model = "allergy") => {
      if (!enabled) return;
      if (!userId) return;
      try {
        if (model === "surgicalHistory") {
          await dispatch(removeSurgicalHistory({ userId, id })).unwrap();
        } else {
          await dispatch(removeAllergyRecord({ userId, id })).unwrap();
        }
        message.success("Allergy removed");
        if (model === "surgicalHistory") {
          dispatch(fetchSurgicalHistory(userId));
        } else {
          setAllergyRecords(await dispatch(fetchAllergies(userId)).unwrap());
        }
      } catch (err) {
        message.error(err?.message || "Failed to remove allergy");
      }
    },
    [dispatch, userId, enabled]
  );

  /* ===== Surgeries ===== */
  const saveSurgery = useCallback(
    async (surgery) => {
      if (!enabled) return;
      if (!userId || surgery.backendId) return;
      try {
        const created = await dispatch(
          addSurgicalHistory({
            userId,
            values: { type: "SURGERY", item: surgery.type, date: surgery.year },
          })
        ).unwrap();
        message.success("Surgery added");
        await dispatch(fetchSurgicalHistory(userId));
        return created;
      } catch (err) {
        message.error(err?.message || "Failed to add surgery");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  const updateSurgeryBackend = useCallback(
    async (id, values) => {
      if (!enabled) return;
      if (!userId) return;
      try {
        const updated = await dispatch(updateSurgicalHistory({ userId, id, values })).unwrap();
        await dispatch(fetchSurgicalHistory(userId));
        return updated;
      } catch (err) {
        message.error(err?.message || "Failed to update surgery");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  const deleteSurgery = useCallback(
    async (id) => {
      if (!enabled) return;
      if (!userId) return;
      try {
        await dispatch(removeSurgicalHistory({ userId, id })).unwrap();
        message.success("Surgery removed");
        dispatch(fetchSurgicalHistory(userId));
      } catch (err) {
        message.error(err?.message || "Failed to remove surgery");
      }
    },
    [dispatch, userId, enabled]
  );

  /* ===== Family history ===== */
  useEffect(() => {
    if (!enabled) return;
    if (!Array.isArray(familyData)) return;

    setFamilyHistory(
      familyData.map((f) => ({
        id: f.id,
        condition: f.condition,
        relation: f.relation,
        backendId: f.id,
      }))
    );
  }, [familyData, enabled]);

  const saveFamilyHistory = useCallback(
    async (family) => {
      if (!enabled) return;
      if (!userId || family.backendId) return;
      try {
        const created = await dispatch(
          addFamilyHistory({
            userId,
            values: { relation: family.relation, condition: family.condition },
          })
        ).unwrap();
        message.success("Family history added");
        await dispatch(fetchFamilyHistory(userId));
        return created;
      } catch (err) {
        message.error(err?.message || "Failed to add family history");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  const updateFamilyHistoryBackend = useCallback(
    async (id, values) => {
      if (!enabled) return;
      if (!userId) return;
      try {
        const updated = await dispatch(updateFamilyHistory({ userId, id, values })).unwrap();
        await dispatch(fetchFamilyHistory(userId));
        return updated;
      } catch (err) {
        message.error(err?.message || "Failed to update family history");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  const deleteFamilyHistory = useCallback(
    async (id) => {
      if (!enabled) return;
      if (!userId) return;
      try {
        await dispatch(removeFamilyHistory({ userId, id })).unwrap();
        message.success("Failed removed");
        dispatch(fetchFamilyHistory(userId));
      } catch (err) {
        message.error(err?.message || "Failed to remove family history");
      }
    },
    [dispatch, userId, enabled]
  );

  /* ===== Chronic conditions ===== */
  useEffect(() => {
    if (!enabled) return;
    if (!medicalData) return;
    if (!medicalData.maladiesChroniques) {
      setChronic([]);
      return;
    }

    try {
      const parsed = JSON.parse(medicalData.maladiesChroniques);
      setChronic(Array.isArray(parsed) ? parsed.map((c, idx) => ({ ...c, id: c.id || idx + 1 })) : []);
    } catch {
      const conditions = String(medicalData.maladiesChroniques).split(";").filter(Boolean);
      setChronic(conditions.map((c, idx) => ({ id: idx + 1, condition: c.trim(), diagnosis: "" })));
    }
  }, [medicalData, enabled]);

  const saveChronicConditions = useCallback(
    async (conditions) => {
      if (!enabled) return;
      if (!userId) return;
      try {
        const updated = await dispatch(
          updateMedicalInfo({
            userId,
            values: { maladiesChroniques: JSON.stringify(conditions) },
          })
        ).unwrap();
        message.success("Chronic conditions updated");
        await dispatch(fetchMedicalInfoByUserId(userId));
        return updated;
      } catch (err) {
        message.error(err?.message || "Failed to update chronic conditions");
        throw err;
      }
    },
    [dispatch, userId, enabled]
  );

  return {
    lifestyle: enabled ? lifestyle : [],
    setLifestyle,
    allergies: enabled ? allergies : [],
    setAllergies,
    surgeries: enabled ? surgeries : [],
    setSurgeries,
    familyHistory: enabled ? familyHistory : [],
    setFamilyHistory,
    chronic: enabled ? chronic : [],
    setChronic,
    loading: enabled ? loading : false,

    updateLifestyle,
    setLifestyleActive,
    saveLifestyle,

    saveAllergy,
    updateAllergyBackend,
    deleteAllergy,

    saveSurgery,
    updateSurgeryBackend,
    deleteSurgery,

    saveFamilyHistory,
    updateFamilyHistoryBackend,
    deleteFamilyHistory,

    saveChronicConditions,
  };
}

export default useHistoryForm;
